/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import clsx from 'clsx';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { showModal } from '../../../../components/EpModal';
import { RECAPTCHA_CREATE_NOTIFICATION } from '../../../../constants/recaptchaConstants';
import { useTranslation as t } from '../../../../hooks/useTranslation';
import PatientSafetySealModal from '../../../../layout/PatientSafetySealModal';
import { notificationService } from '../../../../services/notificationService';
import {
  IGetStampsResponse,
  stampsService,
} from '../../../../services/stampsService';
import {
  useHospitalStore,
  useNotificationStore,
  useToasterStore,
  useTranslationStore,
  useUploadStore,
} from '../../../../stores';
import { NotificationCreateBody } from '../../../../utils';
import FormStepHeader from '../../Utils/FormStepHeader';
import FooterIncidentNotification from './FooterIncidentNotification';
import styles from './FormIncidentNotification.module.scss';
import {
  InputsStepsIncidentNotification,
  NotificationFormType,
  TitleStepsIncidentNotification,
} from './FormIncidentNotificationDTO';
import Step1IncidentNotification from './Steps/Step1';
import Step2IncidentNotification from './Steps/Step2';
import Step3IncidentNotification from './Steps/Step3';
import Step4IncidentNotification from './Steps/Step4';
import Step5IncidentNotification from './Steps/Step5';
import Step6IncidentNotification from './Steps/Step6';

export const FormIncidentNotification = ({
  isPublicTemplate = false,
}: {
  isPublicTemplate?: boolean;
}) => {
  const { addAccessCode } = useNotificationStore((state) => state);
  const { addSingleToast } = useToasterStore((state) => state);
  const { hospitalInfo } = useHospitalStore((state) => state);
  const { translations } = useTranslationStore((state) => state);
  const { removeAll } = useUploadStore((state) => state);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [loadingSubmitButton, setLoadingSubmitButton] = useState(false);

  const searchParams = useSearchParams();
  const hospitalId =
    searchParams.get('HospitalId') || searchParams.get('hospitalId');

  const methods = useForm<NotificationFormType>({
    reValidateMode: 'onSubmit',
    mode: 'onSubmit',
    defaultValues: {
      notificationTypeId: {},
      notificationCategoryId: {},
      notificationIncidentId: {},
      questions: [],
      formUtils: {
        activeStep: 1,
        totalSteps: 5,
        review: false,
        InputsSteps: InputsStepsIncidentNotification,
      },
    },
  });

  const values = methods.getValues();
  const isQualityDeviations =
    values?.notificationCategoryId?.inputValue === '45'
      ? 'quality-deviations'
      : '';
  const { createNotification } = notificationService;
  const {
    data,
    mutateAsync: mutate,
    isSuccess,
    isLoading: isLoadingCreateNotification,
  } = createNotification(
    values,
    isQualityDeviations,
    () => {
      removeAll();
      setLoadingSubmitButton(false);
    },
    () => {
      setLoadingSubmitButton(false);
    },
  );

  //modal--sp-seal-id
  const myStamp: IGetStampsResponse = {} as IGetStampsResponse;
  const { getCurrentStamp } = stampsService;
  const { data: stamp = myStamp, isLoading } = getCurrentStamp({
    hospitalId: hospitalInfo?.HospitalId || hospitalId || '',
    categoryId: process.env.NEXT_PUBLIC_PATIENTY_SAFETY_STAMP_CATEGORY_ID || '',
  });

  const handleNotificationSubmit = async () => {
    if (!executeRecaptcha) {
      console.error('Falha ao executar o recaptcha.');

      return;
    };

    const bodyValues = NotificationCreateBody(values);

    let token = null;

    setLoadingSubmitButton(true);

    token = await executeRecaptcha?.(RECAPTCHA_CREATE_NOTIFICATION);

    if (token) {
      await mutate({
        ...bodyValues,
        hospitalId: hospitalInfo?.HospitalId,
        recaptchaToken: token ? token : null,
      });
    }

    setLoadingSubmitButton(false);
  };

  function handleError(errors: any) {
    if (errors.responsabilityTerm?.type === 'required') {
      addSingleToast({
        caption: t(translations['global'], 'Campos obrigatórios requisitados.'),
        type: 'danger',
      });
    }
  }

  const onSubmit: SubmitHandler<NotificationFormType> = () =>
    handleNotificationSubmit();

  const onError = (errors: any) => handleError(errors);

  const activeStep = useWatch({
    name: 'formUtils.activeStep',
    control: methods.control,
  });

  if (activeStep === methods.getValues('formUtils.totalSteps')) {
    methods.setValue('formUtils.review', true);
  }

  useEffect(() => {
    if (isSuccess) {
      addAccessCode(data);
      methods.setValue('formUtils.activeStep', 6);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit, onError)}
          style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
        >
          <div className={styles['card__header']}>
            <h3>{t(translations['global'], 'Incident Notification Form')}</h3>
            {!isLoading && stamp && (
              <>
                <div className={styles['alert-stamp-sp']}>
                  <div className={styles['alert-stamp-sp-content']}>
                    <p>
                      {t(
                        translations['global'],
                        'This institution won the Patient Safety Certificate, issued by Epimed Solutions in partnership with IBSP. This certificate is awarded to institutions that meet the criteria for promoting good practices for Patient Safety related to the management of incidents and adverse events.',
                      )}
                    </p>
                  </div>
                  <div className={styles['alert-stamp-sp-icon']}>
                    <img
                      onClick={() => showModal('modal--sp-seal-id')}
                      src={
                        process.env.NEXT_PUBLIC_BASE_API_URL_CDN + stamp.imgMin
                      }
                      alt="Selo Segurança do Paciente"
                    />
                  </div>
                </div>

                <PatientSafetySealModal stamp={stamp} />
              </>
            )}
          </div>

          <div className={styles['card__content']}>
            <div
              className={clsx(
                styles['card__content__steps'],
                activeStep > TitleStepsIncidentNotification.length &&
                  styles['not-visible'],
              )}
            >
              <FormStepHeader />
            </div>

            {activeStep === 1 && <Step1IncidentNotification />}
            {activeStep === 2 && <Step2IncidentNotification />}
            {activeStep === 3 && <Step3IncidentNotification />}
            {activeStep === 4 && <Step4IncidentNotification />}
            {activeStep === 5 && <Step5IncidentNotification />}
            {activeStep === 6 && <Step6IncidentNotification />}
          </div>

          {activeStep !== 6 && (
            <div className={styles['__txt']}>
              {t(translations['global'], '* Required Fied')}
            </div>
          )}

          <div className={styles['card__footer']}>
            <FooterIncidentNotification
              isPublicTemplate={isPublicTemplate}
              isLoading={loadingSubmitButton || isLoadingCreateNotification}
            />
          </div>
        </form>
      </FormProvider>
    </>
  );
};
